// ***********************************************************************
// Assembly         : Veriteq.Partner.Web
// Author           : Mike Palmer
// Created          : 06-21-2024
//
// Last Modified By : Mike Palmer
// Last Modified On : 06-21-2024
// ***********************************************************************
// <copyright file="_pagination.js" company="NSA Group Ltd">
//     Copyright (c) NSA Group Ltd. All rights reserved.
// </copyright>
// <summary>
//     JavaScript utilities for handling dynamic pagination in Blazor applications.
// </summary>
// ***********************************************************************

window.blazor = window.blazor || {};

/**
 * Calculates the maximum number of items that can be displayed within a specified element
 * based on the element's height and the root font size, without causing overflow.
 * 
 * @function getItemsPerPage
 * @memberof window.blazor
 * @param {string} elementId - The ID of the container element that holds the list of items.
 * @returns {number} The maximum number of items that can be displayed within the container.
 */
window.blazor.getItemsPerPage = function (elementId) {
    const element = document.getElementById(elementId);

    if (element) {
        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const itemsToDisplay = (element.offsetHeight / (rootFontSize * 4));
        return Math.floor(itemsToDisplay);
    }

    return 0;
};

/**
 * Calculates the maximum number of items that can be displayed within a specified element
 * based on the element's height and the root font size, without causing overflow.
 * 
 * @function initializePagination
 * @memberof window.blazor
 * @param {object} dotNetHelper - The .NET object reference used to invoke methods in Blazor.
 * @param {string} elementId - The ID of the container element that holds the list of items.
 */
window.blazor.initializePagination = function(dotNetHelper, elementId) {
    const maxItems = window.blazor.getItemsPerPage(elementId);
    dotNetHelper.invokeMethodAsync('UpdateItemsPerPageAsync', maxItems);
};

/**
 * Sets up a resize event listener that recalculates the maximum number of items
 * that can be displayed within the specified element whenever the window is resized.
 * The updated item count is then passed to a Blazor component via .NET interop.
 * 
 * @function getUpdatedItemsPerPageOnResize
 * @memberof window.blazor
 * @param {object} dotNetHelper - The .NET object reference used to invoke methods in Blazor.
 * @param {string} elementId - The ID of the container element that holds the list of items.
 * @returns {function} A function that, when called, removes the resize event listener.
 */
window.blazor.getUpdatedItemsPerPageOnResize = function (dotNetHelper, elementId) {
    if(!dotNetHelper || !elementId) return;

    const resizeListener = () => {
        const maxItems = window.blazor.getItemsPerPage(elementId);
        if (typeof maxItems !== 'undefined' && maxItems !== null) {
            dotNetHelper.invokeMethodAsync('UpdateItemsPerPageAsync', maxItems);
        }
    };

    window.addEventListener('resize', resizeListener);

    return () => window.removeEventListener('resize', resizeListener);
};